import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';
import { Link, useNavigate } from 'react-router-dom';

const AdminSchoolManagement = () => {
    const navigate = useNavigate();
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [offerModal, setOfferModal] = useState({ open: false, schoolId: null, schoolName: '' });
    const [offerData, setOfferData] = useState({
        schoolId: '',
        courseIds: [],
        discountType: 'percentage',
        discountValue: '',
        seatCount: '',
        expiresAt: ''
    });
    const [allCourses, setAllCourses] = useState([]);
    const toast = useToast();

    useEffect(() => {
        fetchSchools();
        fetchCourses();
    }, []);

    const fetchSchools = async () => {
        try {
            const res = await axiosClient.get('/org_admin/schools');
            setSchools(res.data.schools);
        } catch (err) {
            toast.error('Failed to load schools');
        } finally {
            setLoading(false);
        }
    };

    const fetchCourses = async () => {
        try {
            const res = await axiosClient.get('/courses');
            setAllCourses(res.data.data.filter(c => c.status === 'approved'));
        } catch (err) {
            toast.error('Failed to load courses');
        }
    };

    const handleCreateOffer = async () => {
        if (!offerData.courseIds.length || !offerData.discountValue || !offerData.seatCount) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            await axiosClient.post(`/org_admin/schools/offers`, {
                ...offerData,
                schoolId: offerModal.schoolId,
                courseId: offerData.courseIds,
                discountType: offerData.discountType,
                discountValue: parseFloat(offerData.discountValue),
                seatCount: parseInt(offerData.seatCount)
            });
            toast.success('Offer created successfully');
            setOfferModal({ open: false, schoolId: null, schoolName: '' });
            setOfferData({
                schoolId: '',
                courseIds: [],
                discountType: 'percentage',
                discountValue: '',
                seatCount: '',
                expiresAt: ''
            });
            fetchSchools();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to create offer');
        }
    };

    if (loading) {
        return (
            <DashboardLayout userRole="admin">
                <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="admin">
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">School Management</h1>
                    <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                        Manage schools, their students, and course offers.
                    </p>
                </div>

                {schools.length > 0 ? (
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                <thead className="bg-neutral-50 dark:bg-neutral-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">School</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Students</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Wallet</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                    {schools.map(school => (
                                        <tr key={school._id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-neutral-900 dark:text-white">
                                                    {school.schoolName}
                                                </div>
                                                <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                                    {school.email}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                {school.contactPerson}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                {school.institutionType || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                {school.studentCount || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                ₦{school.walletBalance?.toLocaleString() || '0.00'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                                <button
                                                    onClick={() => setOfferModal({ open: true, schoolId: school._id, schoolName: school.schoolName })}
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                                >
                                                    Create Offer
                                                </button>
                                                <Link
                                                    to={`/admin/cloud/analytics/organization/${school._id}`}
                                                    state={{ entityName: school.schoolName }}
                                                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400"
                                                >
                                                    View Analytics ADH
                                                </Link>
                                                <button
                                                    onClick={() => navigate(`/admin/schools/${school._id}/analytics`)}
                                                    className="text-green-600 hover:text-green-800 dark:text-green-400"
                                                >
                                                    View Analytics
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700">
                        <div className="text-6xl mb-4">📚</div>
                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No Registered School Yet </h3>
                        <p className="text-neutral-600 dark:text-neutral-400">
                            you will start seeing data once school schema has data
                        </p>
                    </div>
                )}

            </div>

            {/* Offer Modal */}
            {offerModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Create Course Offer for {offerModal.schoolName}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Course
                                </label>
                                <select
                                    multiple
                                    value={offerData.courseIds}
                                    onChange={(e) => setOfferData({ ...offerData, courseIds: Array.from(e.target.selectedOptions).map(opt => opt.value) })}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                >
                                    {allCourses.map(course => (
                                        <option key={course._id} value={course._id}>
                                            {course.title} (₦{course.price.toLocaleString()})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        Discount Type
                                    </label>
                                    <select
                                        value={offerData.discountType}
                                        onChange={(e) => setOfferData({ ...offerData, discountType: e.target.value })}
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed Amount</option>
                                        <option value="free">Free</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        Discount Value
                                    </label>
                                    <input
                                        type="number"
                                        value={offerData.discountValue}
                                        onChange={(e) => setOfferData({ ...offerData, discountValue: e.target.value })}
                                        placeholder="e.g., 50 (for 50%) or 10000 (for ₦10,000 off)"
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Number of Seats
                                </label>
                                <input
                                    type="number"
                                    value={offerData.seatCount}
                                    onChange={(e) => setOfferData({ ...offerData, seatCount: e.target.value })}
                                    placeholder="e.g., 50"
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Expiry Date (Optional)
                                </label>
                                <input
                                    type="date"
                                    value={offerData.expiresAt}
                                    onChange={(e) => setOfferData({ ...offerData, expiresAt: e.target.value })}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                            <button
                                onClick={() => setOfferModal({ open: false, schoolId: null, schoolName: '' })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateOffer}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg"
                            >
                                Create Offer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default AdminSchoolManagement;